import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { C, GRADIENT_BRAND_DARK, soft } from '@parsina/framework/tokens';
import { PageHeader, KpiCard, Dialog, DialogContent, DialogHeader, DialogTitle, DateRangeFilter, useToast, Skeleton } from '@parsina/framework';
import { CreditsHistorySkeleton } from './CreditsSkeleton';
import { useDoctor } from '@parsina/framework/clinical/src/hooks/useDoctor';
import { useWalletBalance } from '@parsina/framework/clinical/src/hooks/useWalletBalance';
import { useWalletTransactions } from '../../hooks/useWalletTransactions';
import { useTopUp } from '../../hooks/useTopUp';
import { readPaymentOutcome, stripPaymentParam } from '../../lib/paymentReturn';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { creditLevel, creditColor, CREDIT_LEVEL_META } from '@parsina/framework/clinical/src/constants/credit';
import { fmtCoins, coinsToMoneyLabel } from '@parsina/framework/clinical/src/constants/creditPricing';
import { useCreditPrices } from '@parsina/framework/clinical/src/lib/useCreditPrices';
import { useCreditPurchase } from '../../hooks/useCreditPurchase';
import { giftCodesEnabled } from '../../lib/runtimeEnv';
import { type } from '@parsina/framework/clinical/src/constants/typography';
import SegmentedControl from '../../components/common/SegmentedControl';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import { CoinGlyph } from '@parsina/framework/clinical/src/components/common/CoinChip';
import TopUpSheet from './TopUpSheet';
import GiftCodeCard from './GiftCodeCard';
import {
  getServiceMeta, transactionLabels, txnTime,
  groupByDay, groupByType, computeStats, serviceBreakdown,
  DATE_PRESETS, rangeToISO,
} from './Credits.shared';

const PAGE_SIZE = 30;
const CARD = { background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, boxShadow: '0 1px 6px rgba(0,0,0,0.05)' };

// ── Balance hero ──────────────────────────────────────────────────────────────
// `onTopUp` absent = no way to buy here: no button, and no money figure, which
// is what the credit would cost to buy. The figure is in the credit's own
// currency (toman for Parsina, as always; `coinsToMoneyLabel`).
function BalanceHero({ balance, onTopUp }) {
  const { t, formatNumber, locale } = useTranslation();
  const { creditPrice } = useCreditPrices();
  const level = creditLevel(balance);
  const color = creditColor(balance);
  // A pending balance is a placeholder block, never loading text: the chip
  // keeps its size and the level label lands in place.
  const statusLabel = balance === null
    ? <Skeleton width={56} height={11} tone="onDark" />
    : t(CREDIT_LEVEL_META[level].labelKey);
  return (
    <div style={{ background: GRADIENT_BRAND_DARK, borderRadius: 20, padding: '22px 24px', color: '#fff', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: -40, left: -40, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
      <div style={{ position: 'absolute', bottom: -60, right: -30, width: 190, height: 190, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
      <div style={{ position: 'relative' }}>
        <div style={{ ...type.label, color: 'rgba(255,255,255,0.6)' }}>{t('credits.currentBalance')}</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginTop: 6 }}>
          <span style={{ ...type.hero }}>{balance === null ? '—' : fmtCoins(balance, formatNumber)}</span>
          <span style={{ ...type.unit, color: 'rgba(255,255,255,0.7)', marginBottom: 6 }}>{t('common.units.coin')}</span>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.12)', borderRadius: 20, padding: '3px 10px', marginBottom: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
            <span style={{ ...type.caption, color: 'rgba(255,255,255,0.85)' }}>{statusLabel}</span>
          </div>
        </div>
        {balance !== null && onTopUp && (
          <div style={{ ...type.caption, color: 'rgba(255,255,255,0.55)', marginTop: 8 }}>{coinsToMoneyLabel(balance, { creditPrice, formatNumber, tomanLabel: t('common.units.toman'), locale })}</div>
        )}
        {onTopUp && (
        <button
          onClick={onTopUp} data-testid="credits-topup"
          style={{ ...type.body, marginTop: 16, display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', borderRadius: 10, padding: '8px 16px', cursor: 'pointer' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.26)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.16)'}
        >
          <CoinGlyph size={14} /> {t('credits.buyCoins')}
        </button>
        )}
      </div>
    </div>
  );
}

// ── Price list rail card ──────────────────────────────────────────────────────
function PriceListCard() {
  const { t, formatNumber } = useTranslation();
  const [open, setOpen] = useState(false);
  // The policy's figures once the list has arrived; the shipped constants until
  // then — a price row never spins and never goes blank.
  const { priceList } = useCreditPrices();
  const rows = open ? priceList : priceList.slice(0, 5);
  return (
    <div style={{ ...CARD, padding: '14px 16px' }}>
      <div style={{ ...type.title, color: C.textPrimary, marginBottom: 10 }}>{t('credits.priceListTitle')}</div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {rows.map((r, i) => (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: i < rows.length - 1 ? `1px solid ${C.border}` : 'none' }}>
            <span style={{ ...type.label, flex: 1, color: C.textSecondary }}>{t(r.labelKey)}</span>
            <span style={{ ...type.label, fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: 4, color: C.teal, whiteSpace: 'nowrap' }}>
              <CoinGlyph size={11} />{fmtCoins(r.cost, formatNumber)}{r.perKey ? ` / ${t(r.perKey)}` : ''}
            </span>
          </div>
        ))}
      </div>
      <button onClick={() => setOpen(o => !o)} style={{ ...type.caption, fontWeight: 700, marginTop: 8, background: 'none', border: 'none', color: C.teal, cursor: 'pointer', padding: 0 }}>
        {open ? t('common.actions.close') : t('common.actions.viewAll')}
      </button>
    </div>
  );
}

// ── Service breakdown rail card ───────────────────────────────────────────────
function ServiceBreakdownCard({ transactions }) {
  const { t, formatNumber } = useTranslation();
  const rows = useMemo(() => serviceBreakdown(transactions, t), [transactions, t]);
  if (!rows.length) return null;
  const max = rows[0].amount || 1;
  return (
    <div style={{ ...CARD, padding: '14px 16px' }}>
      <div style={{ ...type.title, color: C.textPrimary, marginBottom: 12 }}>{t('credits.byService')}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows.map(item => (
          <div key={item.key}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
              <span style={{ ...type.label, fontWeight: 700, flex: 1, color: C.textPrimary }}>{item.meta.label}</span>
              <span style={{ ...type.caption, color: C.textMuted }}>{fmtCoins(item.count, formatNumber)} {t('credits.times')}</span>
              <span style={{ ...type.label, fontWeight: 800, color: item.meta.color }}>{fmtCoins(item.amount, formatNumber)}</span>
            </div>
            <div style={{ height: 5, background: C.bg, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(item.amount / max) * 100}%`, background: item.meta.color, borderRadius: 4, transition: 'width 0.6s cubic-bezier(.4,0,.2,1)' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Transaction row ───────────────────────────────────────────────────────────
function TransactionRow({ txn }) {
  const { t, has, locale, formatNumber, formatDate, formatTime } = useTranslation();
  const meta = getServiceMeta(txn.serviceType, t);
  const IconC = meta.icon;
  const isDebit = txn.type === 'debit';
  const { title, sub } = transactionLabels(txn, { t, has, locale, formatNumber });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: `1px solid ${C.border}` }}
      onMouseEnter={e => e.currentTarget.style.background = C.bg}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <div style={{ width: 34, height: 34, borderRadius: 9, background: meta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <IconC size={16} color={meta.color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...type.body, color: C.textPrimary }}>{title || meta.label}</div>
        <div style={{ ...type.micro, fontWeight: 600, color: C.textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {txnTime(txn.createdAt, formatTime)}{sub ? ` · ${sub}` : ''}
        </div>
      </div>
      <div style={{ ...type.amount, color: isDebit ? C.textSecondary : C.green, flexShrink: 0 }}>
        {isDebit ? '−' : '+'}{fmtCoins(txn.amount, formatNumber)}
      </div>
    </div>
  );
}

const FILTERS = (t) => ([{ id: 'all', label: t('common.state.all') }, { id: 'debit', label: t('credits.spend') }, { id: 'credit', label: t('credits.topUp') }]);
const GROUP_OPTIONS = (t) => ([{ id: 'day', label: t('common.units.day') }, { id: 'type', label: t('credits.serviceType') }]);

// One transaction group's total, signed for display (net: debits spend, credits add).
function TypeGroupHeader({ group }) {
  const { t, formatNumber } = useTranslation();
  const IconC = group.meta.icon;
  const spent = group.total >= 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: C.bg }}>
      <span style={{ width: 22, height: 22, borderRadius: 6, background: group.meta.bg, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        <IconC size={13} color={group.meta.color} />
      </span>
      <span style={{ ...type.label, fontWeight: 800, flex: 1, color: C.textPrimary }}>{group.meta.label}</span>
      <span style={{ ...type.caption, color: C.textMuted }}>{fmtCoins(group.items.length, formatNumber)} {t('dashboard.queue.finishedCount')}</span>
      <span style={{ ...type.amount, color: spent ? C.textSecondary : C.green, minWidth: 52, textAlign: 'end' }}>
        {spent ? '−' : '+'}{fmtCoins(Math.abs(group.total), formatNumber)}
      </span>
    </div>
  );
}

const pagerBtn = (disabled) => ({
  ...type.label,
  height: 34, padding: '0 18px', borderRadius: 9,
  background: disabled ? C.bg : C.card,
  border: `1px solid ${C.border}`,
  color: disabled ? C.textFaint || C.textMuted : C.textPrimary,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.55 : 1,
});

// ── Main ──────────────────────────────────────────────────────────────────────
export default function CreditsWeb({ isMobile = false }) {
  const { t, formatNumber, formatDate } = useTranslation();
  const { doctor } = useDoctor();
  const navigate = useAppNavigate();
  const balance = useWalletBalance(doctor?.id);
  const { startTopUp, busy: payBusy, error: payError } = useTopUp(doctor?.id);
  // Buying credit is offered only where the deployment sells it and this panel
  // has a rail for its price (hooks/useCreditPurchase.js — `purchaseAvailable`
  // from the BFF, Iranian gateways in toman; docs/platform-money/CONTRACT.md).
  // Where it is not, this screen offers none of it: no buy button, no price
  // list, no top-up sheet. The balance and the ledger stay.
  const { canBuy } = useCreditPurchase();

  const [page, setPage] = useState(0);
  const [typeFilter, setTypeFilter] = useState('all');
  const [groupMode, setGroupMode] = useState('day');       // 'day' | 'type'
  const [datePreset, setDatePreset] = useState('all');
  const [dateCustom, setDateCustom] = useState({ from: '', to: '' });
  const [topUpOpen, setTopUpOpen] = useState(false);

  const { startDate, endDate } = useMemo(() => rangeToISO(datePreset, dateCustom), [datePreset, dateCustom]);

  const { transactions, totalCount, loading, error, refetch } = useWalletTransactions(doctor?.id, {
    limit: PAGE_SIZE, offset: page * PAGE_SIZE, startDate, endDate,
    // The ledger runs to thousands of rows, so the type filter has to be applied
    // by the server. Filtering the fetched page instead showed only the matches
    // that happened to fall on the current page, under a count for the whole set.
    type: typeFilter === 'all' ? '' : typeFilter,
  });

  // Gateway return leg.
  //
  // The payment BFF has already verified and settled with the bank by the time
  // the browser lands here — it is the only party that can, since the gateway
  // reports server-to-server and Sepehr's settle step needs a receipt the
  // browser never sees. So this screen reports the outcome and refreshes; it
  // must not try to confirm anything itself.
  const toast = useToast();
  useEffect(() => {
    const settled = readPaymentOutcome();
    if (!settled) return;

    // Strip it immediately so a refresh cannot replay the message — and only
    // it, keeping this screen's own query state and the router's history state.
    stripPaymentParam();

    if (settled === 'success') {
      toast.success(t('credits.paymentDone'));
    } else {
      toast.error(t('errors.payment.failedNoCharge'));
    }
    refetch?.();
  }, [toast, refetch]);

  const stats = useMemo(() => computeStats(transactions), [transactions]);
  // Already filtered by the server; kept as a named value so the render below
  // reads the same as it did.
  const filtered = useMemo(
    () => transactions,
    [transactions],
  );
  const dayGroups  = useMemo(() => groupByDay(filtered, t, formatDate),  [filtered, t, formatDate]);
  const typeGroups = useMemo(() => groupByType(filtered, t), [filtered, t]);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // Changing the server-side window must reset paging, else offset points past the new set.
  const applyDatePreset = (id) => { setDatePreset(id); setPage(0); };
  const applyDateCustom = (from, to) => { setDateCustom({ from, to }); setDatePreset('custom'); setPage(0); };
  const level = creditLevel(balance);
  const topMeta = stats.topService ? getServiceMeta(stats.topService, t) : null;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg, overflowY: 'auto' }}>
      <PageHeader title={t('dashboard.rail.smartCredit')} sub={t('credits.subtitle')}>
        {canBuy && (
        <button
          onClick={() => setTopUpOpen(true)} data-testid="credits-topup-header"
          style={{ ...type.body, display: 'inline-flex', alignItems: 'center', gap: 6, background: C.navy, border: 'none', color: '#fff', borderRadius: 10, padding: '9px 18px', cursor: 'pointer' }}>
          <CoinGlyph size={14} /> {t('credits.buyCoins')}
        </button>
        )}
      </PageHeader>

      {/* Canonical desktop page body (§2.2), same as Accounting/Encounters/
          Settings: full-bleed header, then a padded body that FILLS the page.
          A maxWidth here left the content pinned to the start edge with a band
          of empty background down the left of every wide screen — in RTL a
          capped block sits against the right, so the cap reads as a layout bug
          rather than as a centred column. What gets capped is content INSIDE a
          card, never the page body. */}
      <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 18, width: '100%', boxSizing: 'border-box' }}>

        {/* Alert zone — only mid/low */}
        {balance !== null && (level === 'low' || level === 'mid') && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderRadius: 10,
            background: level === 'low' ? C.redLight : C.amberLight, border: `1px solid ${soft(level === 'low' ? C.red : C.amber, 0.35)}` }}>
            <span style={{ ...type.body, color: level === 'low' ? C.red : C.amber, flex: 1 }}>
              {level === 'low'
                ? t('credits.lowBalanceBanner', { coins: fmtCoins(balance, formatNumber) })
                : t('credits.midBalanceBanner', { coins: fmtCoins(balance, formatNumber) })}
            </span>
            {canBuy && (
            <button onClick={() => setTopUpOpen(true)}
              style={{ ...type.label, fontWeight: 700, background: 'transparent', border: `1px solid ${level === 'low' ? C.red : C.amber}`, color: level === 'low' ? C.red : C.amber, borderRadius: 8, padding: '4px 12px', cursor: 'pointer' }}>
              {t('nav.credits.topUp')}
            </button>
            )}
          </div>
        )}

        {/* Hero + KPI strip */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '300px 1fr', gap: 14, alignItems: 'stretch' }}>
          <BalanceHero balance={balance} onTopUp={canBuy ? () => setTopUpOpen(true) : undefined} />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12 }}>
            <KpiCard label={t('credits.spentThisMonth')} value={fmtCoins(stats.usedThisMonth, formatNumber)} unit={t('common.units.coin')} loading={loading && !transactions.length}
              icon={<span style={{ color: C.teal, display: 'flex' }}><CoinGlyph size={18} /></span>} bg={soft(C.teal, 0.12)} accent={C.textPrimary} />
            <KpiCard label={t('credits.averagePerVisit')} value={stats.avgPerVisit == null ? '—' : fmtCoins(stats.avgPerVisit, formatNumber)} unit={t('common.units.coin')}
              icon={<span style={{ color: C.navy, display: 'flex' }}><CoinGlyph size={18} /></span>} bg={soft(C.navy, 0.1)} accent={C.textPrimary} />
            <KpiCard label={t('credits.mostUsedService')} value={topMeta ? topMeta.label : '—'} unit={topMeta ? t('credits.percentSuffix', { value: fmtCoins(stats.topPct, formatNumber) }) : ''}
              icon={topMeta ? <topMeta.icon size={18} color={topMeta.color} /> : undefined} bg={topMeta ? topMeta.bg : C.bg} accent={C.textPrimary} />
          </div>
        </div>

        {/* Main + rail */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 280px', gap: 14, alignItems: 'start' }}>

          {/* History */}
          <div style={{ ...CARD, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ ...type.title, color: C.textPrimary }}>
                {t('credits.transactions')}{totalCount > 0 && <span style={{ ...type.caption, color: C.textMuted, marginInlineStart: 8 }}>({fmtCoins(totalCount, formatNumber)} {t('common.units.itemsSuffix')}</span>}
              </div>
              <SegmentedControl items={FILTERS(t)} value={typeFilter}
                onChange={(v) => { setTypeFilter(v); setPage(0); }}
                size="sm" ariaLabel={t('credits.filterType')} />
            </div>

            {/* Toolbar: grouping mode + date-range window */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 16px', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ ...type.caption, color: C.textMuted }}>{t('credits.grouping')}</span>
              <SegmentedControl items={GROUP_OPTIONS(t)} value={groupMode} onChange={setGroupMode} size="sm" ariaLabel={t('credits.groupBy')} />
              <div style={{ marginInlineStart: 'auto' }} data-testid="credits-daterange">
                <DateRangeFilter presets={DATE_PRESETS(t)} preset={datePreset} custom={dateCustom} onPreset={applyDatePreset} onCustom={applyDateCustom} />
              </div>
            </div>

            {error && !loading && transactions.length === 0 && (
              <ErrorState error={error} title={t('credits.transactionsUnavailable')} onRetry={refetch} />
            )}
            {loading && transactions.length === 0 && (
              <CreditsHistorySkeleton rows={8} />
            )}
            {!loading && !error && filtered.length === 0 && (
              <div style={{ padding: '44px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>🪙</div>
                <div style={{ ...type.body, color: C.textPrimary, marginBottom: 4 }}>
                  {(typeFilter === 'all' && datePreset === 'all') ? t('credits.noTransactions') : t('errors.empty.transactions')}
                </div>
                <div style={{ ...type.label, color: C.textMuted }}>{t('credits.noTransactionsHint')}</div>
              </div>
            )}

            {/* Grouped by day (chronology) or by service type (with per-type totals) */}
            {!loading && groupMode === 'day' && dayGroups.map(group => (
              <div key={group.key}>
                <div style={{ ...type.caption, fontWeight: 700, padding: '6px 16px', color: C.textMuted, background: C.bg }}>{group.label}</div>
                {group.items.map(txn => <TransactionRow key={txn.id} txn={txn} />)}
              </div>
            ))}
            {!loading && groupMode === 'type' && typeGroups.map(group => (
              <div key={group.key}>
                <TypeGroupHeader group={group} />
                {group.items.map(txn => <TransactionRow key={txn.id} txn={txn} />)}
              </div>
            ))}

            {/* Pager.
                «بارگذاری بیشتر» used to sit here, but the hook REPLACES its data
                rather than appending — so the button silently discarded the rows
                already on screen and offered no way back to them. With a ledger
                of thousands, stepping forward one page at a time with no way
                back is also not a way to find anything. */}
            {/* Always state the window, even on a single page: without it a
                ledger that fits on one page looks unpaged, and a ledger that
                does not gives no sense of scale. */}
            {totalCount > 0 && !loading && (
              <div style={{
                padding: '10px 16px 0', textAlign: 'center',
                ...type.caption, color: C.textMuted,
              }} data-testid="txn-range">
                {t('common.pagination.showing')} {fmtCoins(page * PAGE_SIZE + 1, formatNumber)}–{fmtCoins(Math.min((page + 1) * PAGE_SIZE, totalCount))} {t('common.labels.from')} {fmtCoins(totalCount, formatNumber)} {t('credits.transaction')}
              </div>
            )}

            {totalPages > 1 && !loading && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 12, padding: '12px 16px', borderTop: `1px solid ${C.border}`,
              }}>
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  data-testid="txn-prev"
                  style={pagerBtn(page === 0)}
                >
                  {t('common.actions.previous')}
                </button>

                <span style={{ ...type.label, color: C.textSecondary, whiteSpace: 'nowrap' }}
                  data-testid="txn-page-label">
                  {t('common.pagination.pageLabel')} {fmtCoins(page + 1, formatNumber)} {t('common.labels.from')} {fmtCoins(totalPages, formatNumber)}
                </span>

                <button
                  onClick={() => setPage(p => (p + 1 < totalPages ? p + 1 : p))}
                  disabled={page + 1 >= totalPages}
                  data-testid="txn-next"
                  style={pagerBtn(page + 1 >= totalPages)}
                >
                  {t('common.actions.next')}
                </button>
              </div>
            )}
          </div>

          {/* Rail */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Above the price list on purpose: a doctor arriving from the
                campaign has a code in hand and nothing else to do here. */}
            {/* Not where the deployment runs without the marketing service
                (GIFT_CODES=off): every code would be «not valid». */}
            {giftCodesEnabled() && <GiftCodeCard doctorId={doctor?.id} onRedeemed={() => refetch && refetch()} />}
            {canBuy && <PriceListCard />}
            <ServiceBreakdownCard transactions={transactions} />
            <button onClick={() => navigate('profile', { tab: 'credits' })}
              style={{ ...CARD, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', cursor: 'pointer', textAlign: 'start' }}>
              <span style={{ ...type.label, flex: 1, color: C.textSecondary }}>{t('credits.lowBalanceTitle')}</span>
              <span style={{ ...type.caption, fontWeight: 700, color: C.teal }}>{t('credits.settingsLink')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Top-up dialog */}
      {canBuy && (
      <Dialog open={topUpOpen} onOpenChange={setTopUpOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('credits.buyCoins')}</DialogTitle></DialogHeader>
          <TopUpSheet avgPerVisit={stats.avgPerVisit} balance={balance} onPay={startTopUp} busy={payBusy} error={payError} />
        </DialogContent>
      </Dialog>
      )}
    </div>
  );
}
